import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForward from '@mui/icons-material/ArrowForward';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import { useBlogIndex } from '../../hooks/useBlog';
import { useLanguage } from '../../context/LanguageContext';
import { byEpisodeNumber, byNewest, hasAudio, listTitle, metaLabel } from '../../data/blogHelpers';
import { buildBlogPath, buildBlogPostPath } from '../../utils/blogRoutes';
import { BORDER, INK, MUTED, MUTED_SOFT, SURFACE } from '../../pages/Practice/map/mapTokens';
import { CONTENT_MAX_WIDTH, ctaSecondarySx } from '../../styles/tokens';
import { BLOG_TEASER } from './homeContent';
import { SectionHeading, bandSx } from './homeUi';

/**
 * How many rows one column may show before the band starts to dominate the page.
 *
 * The blog is small today and everything fits, but a homepage section cannot
 * grow without limit — at twenty articles this band would be taller than the
 * pricing table. Past the cap the column keeps its newest rows and the CTA below
 * carries the rest.
 */
const COLUMN_LIMIT = 6;

/**
 * The blog, surfaced on the homepage rather than in the nav.
 *
 * A nav entry costs a permanent slot and pulls people out of the funnel at the
 * top of the page; here the blog is reference material a visitor reaches after
 * the product has already made its case.
 *
 * Split by format, the same division /blog uses, because listening and reading
 * are different intents: a visitor deciding "I'll put this on in the car" should
 * not have to pick the episodes out of a mixed list. The split also keeps the
 * podcast visible, which a single newest-first list does not — publish three
 * articles and the episodes fall off the homepage entirely.
 *
 * Built as rows rather than the cards used on /blog itself: this band sits under
 * the pricing table, and card treatment gave free reading the same weight as the
 * thing being sold. Rows are the same shape ExamTipsTeaser uses, so the page
 * reads as one system.
 *
 * The section removes itself entirely while loading, on error, or when the
 * reader's exam track has no posts — a homepage band that renders a spinner or
 * an apology is worse than one that was never there, and react-snap prerenders
 * the empty state, so nothing half-drawn ends up in the served HTML.
 */
const BlogTeaser = ({ tone }) => {
  const { language } = useLanguage();
  const { data } = useBlogIndex(language);

  const posts = data?.posts ?? [];
  // Episodes run in listening order, articles newest first — the same ordering
  // each section uses on /blog, so arriving there is not a reshuffle.
  const episodes = posts.filter(hasAudio).sort(byEpisodeNumber).slice(0, COLUMN_LIMIT);
  const articles = posts
    .filter((post) => !hasAudio(post))
    .sort(byNewest)
    .slice(0, COLUMN_LIMIT);

  if (episodes.length === 0 && articles.length === 0) return null;

  return (
    <Box component="section" sx={bandSx(tone)}>
      <SectionHeading title={BLOG_TEASER.title} subtitle={BLOG_TEASER.subtitle} />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 3, md: 4 },
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          alignItems: 'start',
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        <PostColumn label="Podcast" posts={episodes} />
        <PostColumn label="Articles" posts={articles} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2,
          mt: 3,
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        <Button
          component={RouterLink}
          to={buildBlogPath()}
          variant="outlined"
          endIcon={<ArrowForward />}
          sx={ctaSecondarySx}
        >
          {BLOG_TEASER.cta}
        </Button>
      </Box>
    </Box>
  );
};

/** One format's rows under a quiet label. An empty format renders nothing. */
const PostColumn = ({ label, posts }) => {
  if (posts.length === 0) return null;

  return (
    <Box>
      <Typography
        component="h3"
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 900,
          letterSpacing: '.16em',
          textTransform: 'uppercase',
          color: MUTED,
          mb: 1.25,
        }}
      >
        {label}
      </Typography>

      <Box sx={{ display: 'grid', gap: { xs: 1.25, md: 1.5 } }}>
        {posts.map((post) => (
          <PostRow key={post.id} post={post} />
        ))}
      </Box>
    </Box>
  );
};

const PostRow = ({ post }) => {
  const isAudio = hasAudio(post);
  const Icon = isAudio ? HeadphonesIcon : ArticleOutlined;
  // The episode number is the one thing the row cannot say implicitly: "10 min"
  // reads as a podcast, but not as which podcast to start with.
  const meta =
    isAudio && post.audio?.episodeNumber ? `Ep. ${post.audio.episodeNumber} · ${metaLabel(post)}` : metaLabel(post);

  return (
    <Box
      component={RouterLink}
      to={buildBlogPostPath(post)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.5,
        bgcolor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        textDecoration: 'none',
        transition: 'box-shadow .2s ease',
        '&:hover': { boxShadow: '0 1px 2px rgba(16,24,40,.05), 0 12px 22px -14px rgba(19,62,135,.35)' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Icon aria-hidden sx={{ fontSize: 19, flex: '0 0 auto', color: INK }} />

      <Typography
        sx={{ flexGrow: 1, minWidth: 0, color: INK, fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.4 }}
      >
        {listTitle(post)}
      </Typography>

      <Typography
        component="span"
        sx={{ flex: '0 0 auto', color: MUTED_SOFT, fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}
      >
        {meta}
      </Typography>
    </Box>
  );
};

export default BlogTeaser;
